import { useMutation } from "@apollo/client/react";
import { Form } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { documentAttachmentPath } from "@path/document_paths";

import { addDangerFlash } from "@/components/flash/flashReducer";
import TTMarkdownEditor from "@/components/TTMarkdownEditor";
import RHErrorMessage from "@/containers/RHErrorMessage";
import TTFormGroup from "@/containers/TTFormGroup";
import { MutateDocumentAttachmentDocument } from "@/gql/graphql";
import handleError, { MutationError } from "@/handleError";
import { useAppDispatch } from "@/hooks";

import type { ValuesType } from "../../types";
import type { DocumentFormProps } from "../../utils";
import Preview from "./Preview";

export default function WorkingInstruction(props: DocumentFormProps) {
  const { t } = useTranslation(["documents", "translation"]);
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<ValuesType>();
  const values = useWatch({ control });

  const [mutateDocumentAttachment] = useMutation(MutateDocumentAttachmentDocument);
  const dispatch = useAppDispatch();
  const document = props.document;

  async function uploadImage(file: File) {
    try {
      const { data } = await mutateDocumentAttachment({
        variables: {
          customerId: document.customerId,
          projectId: document.projectId,
          documentId: document.id,
          id: document.attachments[0]?.id,
          values: { file },
        },
      });

      if (!data?.mutateDocumentAttachment) {
        throw new MutationError();
      }

      return documentAttachmentPath(data.mutateDocumentAttachment);
    } catch (e) {
      dispatch(addDangerFlash(t("translation:global.general_error")));
      handleError(e);
      throw e;
    }
  }

  return (
    <>
      <fieldset>
        <legend>{t("documents:form.content")}</legend>

        <TTFormGroup>
          <Form.Label htmlFor="mdContent">{t("documents:field_names.content")}</Form.Label>
          <TTMarkdownEditor
            value={values.mdContent || ""}
            onChange={(v) => setValue("mdContent", v)}
            handleImageUpload={uploadImage}
          />
          <RHErrorMessage errorMessage={errors.mdContent} namespace="documents" />
        </TTFormGroup>
      </fieldset>

      <Preview {...props} />
    </>
  );
}
